import React, { useState } from "react";
import { useLanguage } from "@/App";
import { Send, CheckCircle, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    budget: "",
    project_type: "",
    message: ""
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5)); // Max 5 files
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // In production, you would upload files to a storage service
      // For now, we just send the form data
      await axios.post(`${API}/contact`, {
        ...formData,
        files: files.map(f => f.name) // Just send file names for now
      });
      setIsSubmitted(true);
      toast.success(language === "nl" ? "Bericht verzonden!" : "Message sent!");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        budget: "",
        project_type: "",
        message: ""
      });
      setFiles([]);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(language === "nl" ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const budgetOptions = ["€1.000 - €5.000", "€5.000 - €10.000", "€10.000 - €25.000", "€25.000+"];
  const projectOptions = language === "nl" 
    ? ["Website", "E-commerce", "AI Automation", "Software Development", "Dashboard", "Anders"]
    : ["Website", "E-commerce", "AI Automation", "Software Development", "Dashboard", "Other"];

  const texts = {
    en: {
      overline: "Get In Touch",
      title: "Let's Build Something",
      titleHighlight: "Amazing",
      subtitle: "Ready to transform your business? Fill out the form and we'll get back to you within 24 hours.",
      name: "Your Name *",
      email: "Email Address *",
      company: "Company Name",
      phone: "Phone Number",
      budget: "Project Budget",
      projectType: "Project Type",
      message: "Tell us about your project *",
      upload: "Upload Briefing",
      uploadHint: "PDF, DOC, or images (max 5 files)",
      submit: "Send Message",
      submitting: "Sending...",
      success: "Message Sent!",
      successText: "We'll be in touch within 24 hours.",
      another: "Send Another Message"
    },
    nl: {
      overline: "Neem Contact Op",
      title: "Laten We Iets",
      titleHighlight: "Geweldigs Bouwen",
      subtitle: "Klaar om je bedrijf te transformeren? Vul het formulier in en we nemen binnen 24 uur contact op.",
      name: "Je Naam *",
      email: "E-mailadres *",
      company: "Bedrijfsnaam",
      phone: "Telefoonnummer",
      budget: "Project Budget",
      projectType: "Project Type",
      message: "Vertel over je project *",
      upload: "Upload Briefing",
      uploadHint: "PDF, DOC, of afbeeldingen (max 5 bestanden)",
      submit: "Verstuur Bericht",
      submitting: "Verzenden...",
      success: "Bericht Verzonden!",
      successText: "We nemen binnen 24 uur contact op.",
      another: "Verstuur Nog Een Bericht"
    }
  };

  const t = texts[language] || texts.en;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-32 bg-[#030303] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,107,0,0.08),transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-[#FF6B00] text-xs font-bold uppercase tracking-[0.3em] mb-4">
              {t.overline}
            </span>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t.title}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
                {t.titleHighlight}
              </span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              {t.subtitle}
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/5">
                <div className="w-12 h-12 bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#FF6B00] text-xl">@</span>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Email</p>
                  <p className="text-white font-medium">{language === "nl" ? "Via contactformulier" : "Via contact form"}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/5">
                <div className="w-12 h-12 bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#FF6B00] text-xl">📍</span>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Adres</p>
                  <p className="text-white font-medium">Torenallee 101</p>
                  <p className="text-white font-medium">5617BR Eindhoven</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/5">
                <div className="w-12 h-12 bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#FF6B00] text-xl">📱</span>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">WhatsApp</p>
                  <p className="text-white font-medium">+31 6 84006668</p>
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="mt-8 p-6 bg-white/[0.02] border border-white/5">
              <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">
                {language === "nl" ? "Bedrijfsgegevens" : "Business Details"}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">KvK</span>
                  <p className="text-white">99681293</p>
                </div>
                <div>
                  <span className="text-zinc-500">BTW</span>
                  <p className="text-white">NL005404414B27</p>
                </div>
                <div className="col-span-2">
                  <span className="text-zinc-500">IBAN</span>
                  <p className="text-white">NL22INGB0116174293</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {isSubmitted ? (
              <div className="bg-[#0A0A0A] border border-[#FF6B00]/30 p-12 flex flex-col items-center justify-center text-center h-full">
                <div className="w-20 h-20 bg-[#FF6B00]/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={40} className="text-[#FF6B00]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {t.success}
                </h3>
                <p className="text-zinc-400 mb-6">{t.successText}</p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-[#FF6B00] font-bold hover:underline"
                >
                  {t.another}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 p-8">
                <div className="space-y-6">
                  {/* Name & Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">{t.name}</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF6B00] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">{t.email}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF6B00] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Company & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">{t.company}</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF6B00] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">{t.phone}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF6B00] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Budget & Project Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">{t.budget}</label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#FF6B00] transition-colors appearance-none cursor-pointer"
                        style={{ backgroundColor: "#0A0A0A" }}
                      >
                        <option value="">Select...</option>
                        {budgetOptions.map((option, i) => (
                          <option key={i} value={option} className="bg-[#0A0A0A]">{option}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">{t.projectType}</label>
                      <select
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#FF6B00] transition-colors appearance-none cursor-pointer"
                        style={{ backgroundColor: "#0A0A0A" }}
                      >
                        <option value="">Select...</option>
                        {projectOptions.map((option, i) => (
                          <option key={i} value={option} className="bg-[#0A0A0A]">{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">{t.message}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF6B00] transition-colors resize-none"
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">{t.upload}</label>
                    <div className="border-2 border-dashed border-white/10 p-6 text-center hover:border-[#FF6B00]/50 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      />
                      <Upload size={24} className="text-zinc-500 mx-auto mb-2" />
                      <p className="text-zinc-400 text-sm">{t.uploadHint}</p>
                    </div>
                    
                    {/* Uploaded files */}
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-white/5 px-4 py-2">
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-[#FF6B00]" />
                              <span className="text-white text-sm truncate max-w-[200px]">{file.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-zinc-500 hover:text-red-500 transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white py-4 font-bold hover:shadow-[0_0_30px_rgba(255,107,0,0.4)] transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? t.submitting : t.submit}
                    <Send size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
